import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { IBlocks } from "@/types";
import * as tabler from "@tabler/icons-react";
import { v4 as uuidv4 } from "uuid";
import { Badge } from "@/components/ui/badge";
interface IBlockCreate {
    description?: string;
    category?: string;
    parent?: string;
    parent_icon?: React.ReactNode;
    icon_react?: React.ReactNode;
    preview?: React.ReactNode;
    preview_icon?: React.ReactNode;
    v2Render?: boolean;
    limited?: string[];
}
export type IBlock = IBlocks & IBlockCreate;
const colour = {
    text: "",
    background: "",
    toggle: false,
};
const blockTypes: IBlock[] = [
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "link",
        name: "Link",
        description: "Link to any page or website",
        category: "General",
        parent: "Links",
        parent_icon: (
            <tabler.IconLink
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconLink
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        preview: (
            <div className="flex w-full items-center gap-2 rounded-md bg-primary p-3">
                <Skeleton
                    className="aspect-square w-8 rounded-md bg-foreground"
                    nopulse
                />
                <Skeleton className="h-5 w-full bg-foreground" nopulse />
            </div>
        ),
        preview_icon: (
            <tabler.IconLink
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-primary group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        url: "",
        icon: "IconLink",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "copy_link",
        name: "Text Copy",
        description: "Click to copy a string of text",
        category: "General",
        parent: "Links",
        parent_icon: (
            <tabler.IconLink
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={26}
            />
        ),
        preview_icon: (
            <tabler.IconCopy
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-primary group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconCopy
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconCopy",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "social_buttons",
        name: "Multi Link",
        description: "A row of links",
        category: "General",
        parent: "Links",
        parent_icon: (
            <tabler.IconLink
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconGripHorizontal
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        preview: (
            <div className="flex w-full items-center justify-evenly gap-2">
                <div className="flex items-center gap-2 rounded-md bg-blue-500 p-3">
                    <tabler.IconBrandBluesky
                        size={32}
                        className="font-bold text-foreground"
                        stroke={2}
                    />
                </div>
                <div className="flex items-center gap-2 rounded-md bg-twitch p-3">
                    <tabler.IconBrandTwitch
                        size={32}
                        className="font-bold text-foreground"
                        stroke={2}
                    />
                </div>
                <div className="flex items-center gap-2 rounded-md bg-youtube p-3">
                    <tabler.IconBrandYoutube
                        size={32}
                        className="font-bold text-foreground"
                        stroke={2}
                    />
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconGripHorizontal
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-primary group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        url: "",
        icon: "IconGripHorizontal",
        colour: {
            ...colour,
            background_block: "transparent",
            position: "center",
        },
        adult: false,
        show: true,
        options: {
            icons: [
                {
                    id: uuidv4(),
                    url: "",
                    icon: "IconTestPipe",
                    style: {
                        enabled: false,
                        text: "",
                        background: "",
                    },
                },
            ],
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "img",
        name: "Image",
        description: "Add an image that can be clicked",
        category: "Images",
        parent: "Images",
        parent_icon: (
            <tabler.IconPhoto
                className="shrink-0 rounded-md bg-tertiary p-1 text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconPhoto
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        preview: (
            <div className="flex aspect-square w-full items-center gap-2 overflow-hidden rounded-md bg-accent p-0">
                <img
                    src="https://gezel.b-cdn.net/placeholders/headshots/20250420_112445.jpg"
                    alt="preview"
                    className="h-full w-full object-cover blur-[2px] grayscale"
                />
            </div>
        ),
        preview_icon: (
            <tabler.IconPhoto
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-tertiary group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        url: "",
        icon: "IconPhoto",
        colour,
        adult: false,
        show: true,
        options: {
            size: 100,
            link: "",
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "gallery",
        name: "Gallery",
        description: "Add images",
        category: "Images",
        parent: "Images",
        parent_icon: (
            <tabler.IconPhoto
                className="shrink-0 rounded-md bg-tertiary p-1 text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconPhoto
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        preview: (
            <div className="grid w-full grid-cols-2 gap-3">
                <div className="aspect-square overflow-hidden rounded-md bg-accent p-0">
                    <img
                        src="https://gezel.b-cdn.net/placeholders/headshots/20250420_112445.jpg"
                        alt="preview"
                        className="h-full w-full object-cover object-left blur-[2px] grayscale"
                    />
                </div>
                <div className="aspect-square overflow-hidden rounded-md bg-accent p-0">
                    <img
                        src="https://gezel.b-cdn.net/placeholders/headshots/20250420_112445.jpg"
                        alt="preview"
                        className="h-full w-full object-cover object-right blur-[2px] grayscale"
                    />
                </div>
                <div className="aspect-square overflow-hidden rounded-md bg-accent p-0">
                    <img
                        src="https://gezel.b-cdn.net/placeholders/headshots/20250420_112445.jpg"
                        alt="preview"
                        className="h-full w-full object-cover object-left blur-[2px] grayscale"
                    />
                </div>
                <div className="aspect-square overflow-hidden rounded-md bg-accent p-0">
                    <img
                        src="https://gezel.b-cdn.net/placeholders/headshots/20250420_112445.jpg"
                        alt="preview"
                        className="h-full w-full object-cover object-right blur-[2px] grayscale"
                    />
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconPhoto
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-tertiary group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        url: "",
        icon: "IconPhoto",
        colour,
        adult: false,
        show: true,
        options: {
            images: [],
            grid_size: 2,
        },
        group: [],
        v2Render: true,
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "carousel",
        name: "Carousel",
        description: "A carousel of images",
        category: "Images",
        parent: "Images",
        parent_icon: (
            <tabler.IconPhoto
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconCarouselHorizontalFilled
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        preview: (
            <div className="relative w-full overflow-x-hidden">
                <div className="flex min-w-0 translate-x-1/4 gap-3 transition-transform duration-500">
                    <div className="aspect-square flex-shrink-0 basis-1/2 overflow-hidden rounded-md bg-accent p-0">
                        <img
                            src="https://gezel.b-cdn.net/placeholders/headshots/20250420_112445.jpg"
                            alt="preview"
                            className="h-full w-full object-cover object-left blur-[2px] grayscale"
                        />
                    </div>
                    <div className="aspect-square flex-shrink-0 basis-1/2 overflow-hidden rounded-md bg-accent p-0">
                        <img
                            src="https://gezel.b-cdn.net/placeholders/headshots/20250420_112445.jpg"
                            alt="preview"
                            className="h-full w-full object-cover object-right blur-[2px] grayscale"
                        />
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconCarouselHorizontalFilled
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-tertiary group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        url: "",
        icon: "IconCarouselHorizontalFilled",
        colour,
        adult: false,
        show: true,
        options: {
            images: [],
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "email",
        name: "Email",
        description:
            "Attach your email address for brands and fans to contact you",
        category: "General",
        parent: "Contact",
        parent_icon: (
            <tabler.IconAt
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconMail
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        preview_icon: (
            <tabler.IconMail
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-primary group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        url: "",
        icon: "IconMail",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "divider",
        name: "Divider",
        description: "Something to separate your blocks",
        category: "Layout",
        parent: "Layout",
        parent_icon: (
            <tabler.IconLayout2
                className="shrink-0 rounded-md bg-[#33b0b6] p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview_icon: (
            <tabler.IconSeparatorHorizontal
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#33b0b6] group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconSeparatorHorizontal
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconSeparatorHorizontal",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "div",
        name: "Div Block",
        description:
            "Add customizable div blocks to your page that are styled with CSS and powered by metadata.",
        category: "Layout",
        parent: "Layout",
        parent_icon: (
            <tabler.IconLayout2
                className="shrink-0 rounded-md bg-[#33b0b6] p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview_icon: (
            <tabler.IconFileTypeHtml
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#33b0b6] group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconFileTypeHtml
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconFileTypeHtml",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
        v2Render: true,
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "textblock",
        name: "Text",
        description: "A basic text block",
        category: "Writing",
        parent: "Blogging & Writing",
        parent_icon: (
            <tabler.IconArticle
                className="shrink-0 rounded-md bg-[#ff7700] p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview_icon: (
            <tabler.IconTypography
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#ff7700] group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconTypography
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "",
        colour,
        adult: false,
        show: true,
        options: {
            content: "",
            bold_text: false,
            center_text: false,
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "rich_text_block",
        name: "Rich Text Block",
        description:
            "Powerful text editor for writing long and formatted paragraphs",
        category: "Writing",
        parent: "Blogging & Writing",
        parent_icon: (
            <tabler.IconArticle
                className="shrink-0 rounded-md bg-[#ff7700] p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview_icon: (
            <tabler.IconTextPlus
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#ff7700] group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconTextPlus
                className="shrink-0 rounded-md bg-[#ff7700] p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconTextPlus",
        colour,
        adult: false,
        show: true,
        options: {
            content: [
                {
                    id: uuidv4(),
                    type: "heading",
                    props: {
                        textColor: "default",
                        backgroundColor: "default",
                        textAlignment: "left",
                        level: 1,
                    },
                    content: [
                        {
                            type: "text",
                            text: "Title",
                            styles: {},
                        },
                    ],
                    children: [],
                },
                {
                    id: uuidv4(),
                    type: "paragraph",
                    props: {
                        textColor: "default",
                        backgroundColor: "default",
                        textAlignment: "left",
                        level: 1,
                    },
                    content: [
                        {
                            type: "text",
                            text: "A fully featured rich text block with all the bells and whistles",
                            styles: {},
                        },
                    ],
                    children: [],
                },
            ],
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "blog_post_single",
        name: "Single Post",
        description: "Highlight a specific blog post",
        category: "Blogging",
        parent: "Blogging & Writing",
        parent_icon: (
            <tabler.IconArticle
                className="shrink-0 rounded-md bg-[#ff7700] p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative flex w-full flex-col gap-0 overflow-hidden rounded-md bg-accent p-3">
                <div className="relative flex w-full items-center gap-2">
                    <Skeleton
                        className="aspect-square w-1/3 rounded-md bg-muted-foreground"
                        nopulse
                    />
                    <div className="flex h-full w-2/3 flex-col gap-1">
                        <Skeleton
                            className=" h-5 w-full bg-muted-foreground"
                            nopulse
                        />
                        <Skeleton
                            className="mt-auto h-5 w-full bg-muted-foreground"
                            nopulse
                        />
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconArticle
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#ff7700] group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconArticle
                className="shrink-0 rounded-md bg-[#ff7700] p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconArticle",
        colour,
        adult: false,
        show: true,
        options: {
            post_id: "",
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "blog_post_latest",
        name: "Latest Post",
        description: "Show your latest blog post",
        category: "Blogging",
        parent: "Blogging & Writing",
        parent_icon: (
            <tabler.IconArticle
                className="shrink-0 rounded-md bg-[#ff7700] p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative flex w-full flex-col gap-0 overflow-hidden rounded-md bg-accent p-3">
                <div className="relative flex w-full items-center gap-2">
                    <Skeleton
                        className="aspect-square w-1/3 rounded-md bg-muted-foreground"
                        nopulse
                    />
                    <div className="flex h-full w-2/3 flex-col gap-1">
                        <Skeleton
                            className=" h-5 w-full bg-muted-foreground"
                            nopulse
                        />
                        <Skeleton
                            className="mt-auto h-5 w-full bg-muted-foreground"
                            nopulse
                        />
                    </div>
                </div>
                <Badge
                    variant={"outline"}
                    className="absolute right-1 top-1 bg-card"
                >
                    New
                </Badge>
            </div>
        ),
        preview_icon: (
            <tabler.IconTextScan2
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#ff7700] group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconTextScan2
                className="shrink-0 rounded-md bg-[#ff7700] p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconTextScan2",
        colour,
        adult: false,
        show: true,
        options: {
            limit: 1,
            name: "",
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "blog_post_all",
        name: "Full Blog",
        description: "Showcase you're entire blog",
        category: "Blogging",
        parent: "Blogging & Writing",
        parent_icon: (
            <tabler.IconArticle
                className="shrink-0 rounded-md bg-[#ff7700] p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative flex w-full flex-col gap-3 p-3">
                <div className="relative flex w-full flex-col gap-0 overflow-hidden rounded-md bg-accent p-3">
                    <div className="relative flex w-full items-center gap-2">
                        <Skeleton
                            className="aspect-square w-1/3 rounded-md bg-muted-foreground"
                            nopulse
                        />
                        <div className="flex h-full w-2/3 flex-col gap-1">
                            <Skeleton
                                className=" h-5 w-full bg-muted-foreground"
                                nopulse
                            />
                            <Skeleton
                                className="mt-auto h-5 w-full bg-muted-foreground"
                                nopulse
                            />
                        </div>
                    </div>
                </div>
                <div className="relative flex w-full flex-col gap-0 overflow-hidden rounded-md bg-accent p-3">
                    <div className="relative flex w-full items-center gap-2">
                        <Skeleton
                            className="aspect-square w-1/3 rounded-md bg-muted-foreground"
                            nopulse
                        />
                        <div className="flex h-full w-2/3 flex-col gap-1">
                            <Skeleton
                                className=" h-5 w-full bg-muted-foreground"
                                nopulse
                            />
                            <Skeleton
                                className="mt-auto h-5 w-full bg-muted-foreground"
                                nopulse
                            />
                        </div>
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconNews
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#ff7700] group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconNews
                className="shrink-0 rounded-md bg-[#ff7700] p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconNews",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "mediakit",
        name: "Media Kit",
        description: "Showcase your social media kit",
        category: "General",
        parent: "Mediakit",
        parent_icon: (
            <tabler.IconGraph
                className="shrink-0 rounded-md bg-[#1f74c3] p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative flex aspect-video w-full flex-col gap-3 p-3">
                <div className="relative flex w-full flex-col gap-0 overflow-hidden rounded-md bg-accent p-3">
                    <div className="relative flex w-full items-center gap-2">
                        <div className="relative aspect-square w-1/5">
                            <img
                                src="https://cdn.doras.to/placeholders/headshots/40.webp"
                                loading="lazy"
                                alt="Preview"
                                className="aspect-square w-full rounded-full bg-muted"
                            />
                            <tabler.IconBrandTwitch className="absolute bottom-1 right-1 rounded-md bg-twitch p-0.5" />
                        </div>
                        <div className="flex h-full w-4/5 flex-col gap-1">
                            <Label
                                variant={"toggle"}
                                className="text-lg font-bold"
                            >
                                4.2k Followers
                            </Label>
                            <Label variant={"toggle"}>1k Subscribers</Label>
                            <Label variant={"toggle"}>
                                Average Viewers: 821
                            </Label>
                        </div>
                    </div>
                </div>
                <div className="relative flex w-full flex-col gap-0 overflow-hidden rounded-md bg-accent p-3">
                    <div className="relative flex w-full items-center gap-2">
                        <div className="relative aspect-square w-1/5">
                            <img
                                src="https://cdn.doras.to/placeholders/headshots/40.webp"
                                loading="lazy"
                                alt="Preview"
                                className="aspect-square w-full rounded-full bg-muted"
                            />
                            <tabler.IconBrandYoutube className="absolute bottom-1 right-1 rounded-md bg-youtube p-0.5" />
                        </div>
                        <div className="flex h-full w-4/5 flex-col gap-1">
                            <Label
                                variant={"toggle"}
                                className="text-lg font-bold"
                            >
                                294k Subscribers
                            </Label>
                            <Label variant={"toggle"}>8.92M Views</Label>
                            <Label variant={"toggle"}>
                                Average Watch Duration: 72.9%
                            </Label>
                        </div>
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconNews
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#ff7700] group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconGraph
                className="shrink-0 rounded-md bg-[#1f74c3] p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconGraph",
        colour,
        adult: false,
        show: true,
        options: {
            data: [],
            total_followers: true,
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "twitch",
        name: "Twitch Stream",
        description: "Embed a stream from Twitch",
        category: "Livestreaming",
        parent: "Video & Livestreams",
        icon_react: (
            <tabler.IconBrandTwitch
                className="shrink-0 rounded-md bg-purple-500 p-1 text-primary-foreground"
                size={40}
            />
        ),
        parent_icon: (
            <tabler.IconVideo
                className="shrink-0 rounded-md bg-accent-lighter p-1 text-primary-foreground"
                size={26}
            />
        ),
        preview: (
            <div className="relative flex w-full flex-col gap-0 overflow-hidden">
                <Skeleton
                    className="aspect-video h-full w-full rounded-md border border-twitch bg-gradient-to-br from-twitch/50 to-purple-700/50"
                    nopulse
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-1 rounded-md bg-twitch p-1">
                        <tabler.IconBrandTwitch className="" size={16} />
                        <Label variant={"toggle"}>Live</Label>
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconBrandTwitch
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-twitch group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        url: "",
        icon: "IconBrandTwitch",
        colour,
        adult: false,
        show: true,
        options: {
            hide_offline: false,
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "kick",
        name: "Kick Stream",
        description: "Embed a stream from Kick",
        category: "Livestreaming",
        parent: "Video & Livestreams",
        icon_react: (
            <tabler.IconBrandKick
                className="shrink-0 rounded-md bg-[#53fc18] p-1 text-primary-foreground"
                size={40}
            />
        ),
        parent_icon: (
            <tabler.IconVideo
                className="shrink-0 rounded-md bg-accent-lighter p-1 text-primary-foreground"
                size={26}
            />
        ),
        preview: (
            <div className="relative flex w-full flex-col gap-0 overflow-hidden">
                <Skeleton
                    className="aspect-video h-full w-full rounded-md border border-[#53fc18] bg-gradient-to-br from-[#53fc18]/50 to-lime-700/50"
                    nopulse
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-1 rounded-md bg-[#53fc18] p-1">
                        <tabler.IconBrandKick className="text-card" size={16} />
                        <Label variant={"toggle"} className="text-card">
                            Live
                        </Label>
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconBrandKick
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#53fc18] group-hover/item:text-card"
                size={26}
            />
        ),
        url: "",
        icon: "IconBrandKick",
        colour,
        adult: false,
        show: true,
        options: {
            hide_offline: false,
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "youtube",
        name: "YouTube Stream",
        description: "Embed a stream from YouTube",
        category: "Livestreaming",
        parent: "Video & Livestreams",
        parent_icon: (
            <tabler.IconVideo
                className="shrink-0 rounded-md bg-accent-lighter p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative flex w-full flex-col gap-0 overflow-hidden">
                <Skeleton
                    className="aspect-video h-full w-full rounded-md border border-youtube bg-gradient-to-br from-youtube/50 to-red-700/50"
                    nopulse
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-1 rounded-md bg-youtube p-1 text-foreground">
                        <tabler.IconBrandYoutubeFilled className="" size={16} />
                        <Label variant={"toggle"} className="text-foreground">
                            Live
                        </Label>
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconBrandYoutubeFilled
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-youtube group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconBrandYoutube
                className="shrink-0 rounded-md bg-red-500 p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconBrandYoutube",
        colour,
        adult: false,
        show: true,
        options: {
            hide_offline: false,
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "vrcdn_live",
        name: "VRCDN",
        description: "Embed a stream from vrcdn.live",
        category: "Livestreaming",
        parent: "Video & Livestreams",
        parent_icon: (
            <tabler.IconVideo
                className="shrink-0 rounded-md bg-accent-lighter p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview_icon: (
            <tabler.IconBadgeVrFilled
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#3be2e2] group-hover/item:text-card"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconBadgeVrFilled
                className="shrink-0 rounded-md bg-[#3be2e2] p-1 text-background"
                size={40}
            />
        ),
        url: "",
        icon: "IconBadgeVrFilled",
        colour,
        adult: false,
        show: true,
        options: {
            hide_offline: false,
        },
        group: [],
    },

    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "youtube_video",
        name: "YouTube Video",
        description: "Embed a specific YouTube video",
        category: "Video",
        parent: "Video & Livestreams",
        parent_icon: (
            <tabler.IconVideo
                className="shrink-0 rounded-md bg-accent-lighter p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative flex w-full flex-col gap-0 overflow-hidden">
                <Skeleton
                    className="aspect-video h-full w-full rounded-md border border-youtube bg-gradient-to-br from-youtube/50 to-red-700/50"
                    nopulse
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-1 rounded-md bg-youtube p-1 text-foreground">
                        <tabler.IconBrandYoutubeFilled className="" size={16} />
                        <Label variant={"toggle"} className="text-foreground">
                            Video
                        </Label>
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconBrandYoutubeFilled
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-youtube group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconBrandYoutube
                className="shrink-0 rounded-md bg-red-500 p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconBrandYoutube",
        colour,
        adult: false,
        show: true,
        options: {
            hide_title: true,
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "youtube_video_latest",
        name: "Latest YouTube",
        description: "Embed the latest YouTube video uploaded from a channel",
        category: "Video",
        parent: "Video & Livestreams",
        parent_icon: (
            <tabler.IconVideo
                className="shrink-0 rounded-md bg-accent-lighter p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative flex w-full flex-col gap-0 overflow-hidden">
                <Skeleton
                    className="aspect-video h-full w-full rounded-md border border-youtube bg-gradient-to-br from-youtube/50 to-red-700/50"
                    nopulse
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-1 rounded-md bg-youtube p-1 text-foreground">
                        <tabler.IconBrandYoutubeFilled className="" size={16} />
                        <Label variant={"toggle"} className="text-foreground">
                            Latest Video
                        </Label>
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconBrandYoutubeFilled
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-youtube group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconBrandYoutube
                className="shrink-0 rounded-md bg-red-500 p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconBrandYoutube",
        colour,
        adult: false,
        show: true,
        options: {
            hide_title: false,
            show_custom_title: false,
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "spotify",
        name: "Spotify",
        description: "Embed a Spotify playlist, song, album or artist",
        category: "Embed",
        parent: "Integrations",
        parent_icon: (
            <tabler.IconPlugConnected
                className="shrink-0 rounded-md bg-sky-500 p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative flex w-full flex-col gap-0 overflow-hidden rounded-md bg-accent p-3">
                <div className="flex w-full gap-2">
                    <Skeleton
                        className="aspect-square w-1/3 rounded-md bg-muted-foreground"
                        nopulse
                    />
                    <div className="flex w-2/3 flex-col gap-1">
                        <Skeleton
                            className="h-5 w-3/4 bg-muted-foreground"
                            nopulse
                        />
                        <Skeleton
                            className="h-5 w-1/2 bg-muted-foreground"
                            nopulse
                        />
                        <div className="mt-auto flex items-center gap-2">
                            <tabler.IconPlayerSkipBack className="ml-auto rounded-full bg-transparent p-1" />
                            <tabler.IconPlayerSkipForward className="rounded-full bg-transparent p-1" />
                            <tabler.IconPlayerPlayFilled className="rounded-full bg-foreground p-1 text-background" />
                        </div>
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconBrandSpotify
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-success group-hover/item:text-card"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconBrandSpotify
                className="shrink-0 rounded-md bg-success p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconBrandSpotify",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "tidal",
        name: "Tidal",
        description: "Embed a Tidal playlist, song, album or artist",
        category: "Embed",
        parent: "Integrations",
        parent_icon: (
            <tabler.IconPlugConnected
                className="shrink-0 rounded-md bg-sky-500 p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative flex w-full flex-col gap-0 overflow-hidden rounded-md bg-accent p-3">
                <div className="flex w-full gap-2">
                    <Skeleton
                        className="aspect-square w-1/3 rounded-md bg-muted-foreground"
                        nopulse
                    />
                    <div className="flex w-2/3 flex-col gap-1">
                        <Skeleton
                            className="h-5 w-3/4 bg-muted-foreground"
                            nopulse
                        />
                        <Skeleton
                            className="h-5 w-1/2 bg-muted-foreground"
                            nopulse
                        />
                        <div className="mt-auto flex items-center gap-2">
                            <tabler.IconPlayerSkipBack className="ml-auto rounded-full bg-transparent p-1" />
                            <tabler.IconPlayerSkipForward className="rounded-full bg-transparent p-1" />
                            <tabler.IconPlayerPlayFilled className="rounded-full bg-foreground p-1 text-background" />
                        </div>
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconBrandTidal
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-white group-hover/item:text-card"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconBrandTidal
                className="shrink-0 rounded-md bg-success p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconBrandTidal",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
        v2Render: true,
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "github_user",
        name: "GitHub Stats",
        description: "Showcase your GitHub stats",
        category: "Embed",
        parent: "Integrations",
        parent_icon: (
            <tabler.IconPlugConnected
                className="shrink-0 rounded-md bg-sky-500 p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview_icon: (
            <tabler.IconBrandGithub
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-black group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconBrandGithub
                className="shrink-0 rounded-md bg-black p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconBrandGithub",
        colour,
        adult: false,
        show: true,
        options: {
            rank: true,
            stars: true,
            prs: true,
            issues: true,
            contribs: true,
        },
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "podbean",
        name: "PodBean",
        description: "Showcase your podcast and latest episode information",
        category: "Embed",
        parent: "Integrations",
        parent_icon: (
            <tabler.IconRss
                className="shrink-0 rounded-md bg-sky-500 p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview_icon: (
            <tabler.IconRss
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#8fc31f] group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconRss
                className="shrink-0 rounded-md bg-[#8fc31f] p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconRss",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "twitter_feed",
        name: "Twitter/X Feed",
        description: "Embed a Twitter feed",
        category: "Embed",
        icon_react: (
            <tabler.IconBrandX
                className="shrink-0 rounded-md bg-black p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconBrandX",
        colour,
        adult: false,
        show: true,
        options: {},
        group: [],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "fourthwall_products",
        name: "Fourthwall Products",
        description: "Embed products from Fourthwall",
        category: "Embed",
        parent: "Integrations",
        parent_icon: (
            <tabler.IconPlugConnected
                className="shrink-0 rounded-md bg-sky-500 p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview: (
            <div className="relative h-full w-full p-9 px-3">
                <div className="grid h-full w-full grid-cols-3 gap-2">
                    <div className="relative col-span-2">
                        <Skeleton
                            className="h-full w-full rounded-md bg-muted"
                            nopulse
                        />
                        <Skeleton
                            className="absolute bottom-1 left-1 h-4 w-2/3 rounded-md bg-accent"
                            nopulse
                        />
                    </div>
                    <div className="relative">
                        <Skeleton
                            className="h-full w-full rounded-md bg-muted"
                            nopulse
                        />
                        <Skeleton
                            className="absolute bottom-1 left-1 h-4 w-2/3 rounded-md bg-accent"
                            nopulse
                        />
                    </div>
                    <div className="relative">
                        <Skeleton
                            className="h-full w-full rounded-md bg-muted"
                            nopulse
                        />
                        <Skeleton
                            className="absolute bottom-1 left-1 h-4 w-2/3 rounded-md bg-accent"
                            nopulse
                        />
                    </div>
                    <div className="relative col-span-2">
                        <Skeleton
                            className="h-full w-full rounded-md bg-muted"
                            nopulse
                        />
                        <Skeleton
                            className="absolute bottom-1 left-1 h-4 w-2/3 rounded-md bg-accent"
                            nopulse
                        />
                    </div>
                </div>
            </div>
        ),
        preview_icon: (
            <tabler.IconShoppingBag
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-muted group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconShoppingBag
                className="shrink-0 rounded-md bg-black p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconShoppingBag",
        colour,
        adult: false,
        show: true,
        options: {
            items: [],
        },
        group: [],
        limited: ["premium", "bplus", "bpro"],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "form",
        name: "Form Popup",
        description: "A popup block that will open a form you've built",
        category: "General",
        parent: "Contact",
        parent_icon: (
            <tabler.IconAt
                className="shrink-0 rounded-md bg-primary p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview_icon: (
            <tabler.IconAddressBook
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-tertiary group-hover/item:text-primary-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconAddressBook
                className="shrink-0 rounded-md bg-tertiary p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconAddressBook",
        colour,
        adult: false,
        show: true,
        options: {
            items: [],
        },
        group: [],
        limited: ["premium", "bplus", "bpro"],
    },
    {
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: "throne_wishlist",
        name: "Throne Wishlist",
        description: "Embed products from Throne",
        category: "Embed",
        parent: "Integrations",
        parent_icon: (
            <tabler.IconPlugConnected
                className="shrink-0 rounded-md bg-sky-500 p-1 text-primary-foreground"
                size={26}
            />
        ),

        preview_icon: (
            <tabler.IconShoppingBag
                className="shrink-0 rounded-md bg-accent p-1 text-muted-foreground transition-all group-hover/item:bg-[#d8369a] group-hover/item:text-foreground"
                size={26}
            />
        ),
        icon_react: (
            <tabler.IconShoppingBag
                className="shrink-0 rounded-md bg-[#d8369a] p-1 text-primary-foreground"
                size={40}
            />
        ),
        url: "",
        icon: "IconShoppingBag",
        colour,
        adult: false,
        show: true,
        options: {
            username: "",
            user_id: "",
        },
        group: [],
        limited: ["premium", "bplus", "bpro"],
    },
];
export default blockTypes;
